import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import type { FormlyFieldConfig } from '@ngx-formly/core';

import { isEmptyValue } from '../../../../utils/empty-value.util';
import { formatDisplayValue } from '../../review-value.util';
import { displayContextFor } from '../display-context.util';
import { isOverviewVisible } from '../overview.types';
import { ReadonlyDisplayBase } from './readonly-display.base';
import { ValueDisplayComponent } from './value-display.component';

/**
 * Read display for the seven date/hours COMPOSITES — `availability`, `cron`, `week`,
 * `week_time`, `date_hours_calculator`, `week-day-hours-number`, `week-day-hours-time`
 * (#21239, task 5).
 *
 * Every other read display formats a SCALAR. These seven store an object whose meaning lives in
 * the sub-field LABELS, and those labels are on `field.fieldGroup` (`fieldGroup.config.ts`), not
 * in the value — so this display walks the FIELD CONFIG alongside the value instead of
 * stringifying the value alone. What the generic join in `formatObjectValue`
 * (`review-value.util.ts`) produced instead, measured 2026-09-21 with the real formatter:
 * - `availability` → `40 8.5` — the whole schedule gone, because that join keeps only
 *   string/number leaves and drops every NESTED object (the rooster lives two levels down);
 * - `week-day-hours-time` → `8 ma 08:00-16:00 8 0`;
 * - `cron` → `14-09-2026 09:00 18-09-2026 17:00 2 weekly`;
 * - `week` → `2026-W38 14-09-2026 20-09-2026 23:59`;
 * - `week-day-hours-number` → `40 ma-vr 8 uur 8 8 8 8 8`.
 *
 * This fix covers the read row only. `wizard-review.component.ts:147` and
 * `readonly-onboarding.component.ts:1618` both bypass `READONLY_DISPLAYS` entirely and hand the
 * whole composite value straight to `formatDisplayValue`, so those two surfaces still render the
 * old generic join — `availability` still reads `40 16.00` there. Unlike
 * `geolocation-display.component.ts` (which shares its coordinate formatter with those same two
 * surfaces), these seven composites have no such shared sub-field formatter yet.
 *
 * ONE generic display for all seven, not seven bespoke ones (user decision 2026-09-21): the
 * shape that makes them unreadable is the same in each, and the fix is the same — put the label
 * back in front of the value.
 *
 * Leaf text is NOT reimplemented here: every leaf goes through the shared `formatDisplayValue`
 * with its OWN `displayContextFor(subField)`, so a date reads `14-09-2026`, a boolean reads
 * `Ja`/`Nee`, an option reads its label ('Wekelijks', not 'weekly') and an addon ('weken')
 * comes along — identical to how the same value would read as a row of its own.
 *
 * Falls back to `<dd-value-display>` when the walk yields nothing: a value whose keys do not
 * match the configured sub-fields (legacy/imported data) must not read as emptier than it did
 * before this display existed.
 *
 * Presentational: both inputs come from the row, the sub-field configs from `field.fieldGroup`,
 * and the option/taxonomy context from each sub-field itself (`displayContextFor`) — no
 * injected store (Global Constraint 1).
 */
@Component({
  selector: 'dd-composite-display',
  imports: [ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the display must not become a box inside the value span, or the row's
  // truncate/multiline behaviour would stop at this element.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    @if (text(); as text) {
      {{ text }}
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class CompositeDisplayComponent extends ReadonlyDisplayBase {
  /**
   * The labelled composite as one string, or `null` when the walk found nothing to show — which
   * is the only case that falls back to `<dd-value-display>`. The string carries no line breaks
   * of its own: the row wraps it, because these seven types are in `MULTILINE_TYPES`
   * (`readonly-field-row.component.ts`).
   */
  readonly text = computed(() => formatCompositeGroup(this.field().fieldGroup, this.value(), 0));
}

/**
 * Between two sub-answers. A middle dot rather than a comma, because the shared leaf formatter
 * ALREADY uses `, ` inside a single answer: a multi-select leaf renders as
 * `Dagen van de week: Maandag, Woensdag` (`formatCoreValue`'s array branch) and a geolocation as
 * `52.37, 4.89`. Reusing the comma between PAIRS would make "still the same answer" and "next
 * answer" look identical. The dot is also narrow and unlike sentence punctuation, which keeps a
 * long composite scannable when it wraps over several lines — these seven types are in
 * `MULTILINE_TYPES` (`readonly-field-row.component.ts`) precisely because they do not fit on one
 * (fix round 1): a truncated schedule shows the reader less than one full day.
 */
const PART_SEPARATOR = ' · ';

/** Between a sub-field's label and its value. */
const LABEL_SEPARATOR = ': ';

/**
 * Sub-field types that stay LEAVES even though they carry a `fieldGroup` of their own, because
 * `formatDisplayValue` already renders each of them as ONE answer and does it better than a
 * labelled walk would: `date_range` as `14-09-2026 – 18-09-2026`, `date_offset` as
 * `3 dagen achteraf`, `geolocation` as `52.37, 4.89`, `rdw`/`kvk` as their primary key, and
 * `address`/`name` assembled from their parts. None of the seven registered types nests one of
 * these today; the set exists so that a config which one day does keeps the better text instead
 * of being flattened into labelled pieces.
 */
const SELF_FORMATTING_COMPOSITE_TYPES = new Set([
  'address',
  'name',
  'rdw',
  'kvk',
  'geolocation',
  'date_range',
  'date_offset',
]);

/**
 * Hard stop on the recursion. The deepest REAL config is three levels
 * (`availability` → `availabilityOdd` (a `week_time`) → `maandag` → `start`), so this cap can
 * never truncate a configured composite. It exists only so that a malformed config — a
 * `fieldGroup` that reaches itself, carrying a value object that does the same — cannot hang the
 * read page; a config that comes from JSON cannot cycle, but nothing in the type system says so.
 */
const MAX_DEPTH = 8;

/**
 * One level of the walk: every sub-field, in CONFIGURED order, joined with `PART_SEPARATOR`.
 * `null` when nothing survived the skips.
 *
 * With one cross-sibling rule on top (fix round 1, narrowed in round 2): a GATE is dropped when a
 * sibling in the same group carries a real answer. `WEEKTIME_FIELDS` gives every day row an
 * `enabled` checkbox labelled with the day, and the two times beside it already prove the day is
 * on, so `maandag: Ja · Maandag van: 08:00 · Maandag tot: 16:00` spends a third of a fourteen-day
 * schedule restating the day name in two casings.
 *
 * A gate is a ticked leaf that the CONFIG says gates its siblings — see `isGate`. That structural
 * test is the round-2 narrowing: the first version asked only "is this leaf `true`?", which would
 * have dropped any meaningful boolean standing next to a filled sibling. Across the seven real
 * configs the two predicates agree (the only boolean leaves are the day gates), but this display
 * is also reachable through `props.readonlyDisplay: 'composite'` on a fieldGroup nobody has seen
 * — the same exposure that made the label-adoption idea unsafe (`formatSubField`), so it gets the
 * same treatment rather than a different standard.
 *
 * A gate is only dropped when something else survived: a day ticked WITHOUT hours still reads
 * `maandag: Ja`, because then the tick is the entire answer. And only a TICKED gate, never
 * `false`: an unticked box beside filled siblings is contradictory data, and hiding it would hide
 * the contradiction. (A group that is nothing BUT unticked boxes is already gone — see
 * `formatSubField`'s emptiness rule.)
 *
 * Residual exposure, stated rather than waved away: a config CAN condition a sibling on a boolean
 * whose own "Ja" the reader wants to keep. It is a narrow shape — the boolean must be a
 * `compareField` of a sibling AND that sibling must be answered — and in that shape the sibling's
 * presence is exactly what the boolean asserts, which is why it is acceptable here and the
 * value-only predicate was not.
 */
function formatCompositeGroup(fields: FormlyFieldConfig[] | undefined, value: unknown, depth: number): string | null {
  if (!fields?.length || depth >= MAX_DEPTH || !isPlainObject(value)) {
    return null;
  }
  const parts: CompositePart[] = [];
  for (const subField of fields) {
    const part = formatSubField(subField, value, depth, fields);
    if (part) {
      parts.push(part);
    }
  }
  const answered = parts.filter(part => !part.isGate);
  const kept = answered.length > 0 ? answered : parts;
  return kept.length > 0 ? kept.map(part => part.text).join(PART_SEPARATOR) : null;
}

/**
 * One rendered sub-answer. `isGate` marks the "ticked" leaves the rule above may drop — see
 * `formatCompositeGroup`.
 */
interface CompositePart {
  text: string;
  isGate: boolean;
}

/**
 * One sub-field as `Label: value`, as `Label (inner · inner)` for a nested group, or `null` when
 * it is skipped. The four skips, each for its own reason:
 *
 * - **no key** — a layout-only sub-field. `type: 'title'` sits inside `AVAILABILITY_FIELDS`
 *   ('Werkdagen') and inside every `WEEKTIME_FIELDS` day row ('tot'); it draws a caption and
 *   holds no value at all, so there is nothing to label.
 * - **not visible** — `field.hide` or a `hidden` CLASS token, via the read view's own
 *   `isOverviewVisible` (`overview.types.ts`). The class is how the form hides a derived cache
 *   it still submits: `WEEK_FIELDS` hides the start/end dates it computes from the picked week,
 *   `CRON_FIELDS` hides `offset`/`endDate`, `WEEK_DAY_HOURS_FIELDS_*` hide `total-hours`,
 *   `summary` and the per-day number that mirrors the times, and `GEOLOCATION_FIELDS` hides the
 *   JSON cache it patches on every change. A cache is not an answer.
 *
 *   NOT a `TECHNICAL_SUBKEYS` check (review round 2): that list is keyed by VALUE key and exists
 *   for `formatObjectValue`, which walks a value without any config. This walk goes over the
 *   CONFIG, where plumbing never appears as a sub-field of its own and every real cache is
 *   hidden — so a key-name filter here could only ever remove a genuine, visible, labelled
 *   sub-field that happens to be keyed `geolocation` (or `id`), the natural key an admin gives a
 *   nested geolocation composite. Its coordinates would then vanish from a list that promises to
 *   render exactly that (`SELF_FORMATTING_COMPOSITE_TYPES`).
 * - **empty value** — by the SHARED `isEmptyValue`. For a LEAF, `false` and `0` count as real
 *   answers (that function's own default, and the rule every other read row follows): a lone
 *   "Nee" is something the user said. For a GROUP, `treatFalseAsEmpty` is passed, so a group
 *   holding nothing but unticked switches and blanks is skipped whole. A group is a container,
 *   not an answer: printing it costs the row `woensdag: Nee · donderdag: Nee · …` for all
 *   fourteen day rows of an `availability` schedule, burying the two days that DO have hours in
 *   twelve lines of "no". Nothing is swallowed by this — the leaf rule is untouched, so a boolean
 *   answer of its own still reads `Nee`.
 *
 * A LEAF with no label is skipped as well, and that is the rule that retires `40 8.5`: an
 * unlabelled number in a labelled list is exactly the meaningless fragment this display exists
 * to replace. In practice it is always a derived total — `availabilityOddTotalHours` /
 * `availabilityEvenTotalHours` carry no `props.label`, only a `hintEnd`. `props.ariaLabel`
 * counts as a label: `WEEKTIME_FIELDS` deliberately leaves its time inputs without a visible one
 * (the day sits on the checkbox to their left) and names them 'Maandag van' / 'Maandag tot' for
 * screen readers — the same accessible name is what the read row needs.
 *
 * A nested GROUP with no label is NOT skipped: its children carry their own labels, so it is
 * inlined into the parent's list. That is what keeps the `availability` rooster: the day rows
 * inside `WEEKTIME_FIELDS` are keyed `maandag`…`zondag` with `props.label: ''`.
 */
function formatSubField(
  subField: FormlyFieldConfig,
  parent: Record<string, unknown>,
  depth: number,
  siblings: FormlyFieldConfig[]
): CompositePart | null {
  const key = subField.key;
  if (key === undefined || key === null) {
    return null;
  }
  const name = String(key);
  if (!isOverviewVisible(subField)) {
    return null;
  }
  const raw = parent[name];
  const isGroup = walksInto(subField, raw);
  if (isEmptyValue(raw, isGroup)) {
    return null;
  }

  const label = labelOf(subField);

  if (isGroup) {
    const inner = formatCompositeGroup(subField.fieldGroup, raw, depth + 1);
    if (!inner) {
      return null;
    }
    // Parentheses, not a second separator level: they say which parts belong to this group at
    // any depth without the row having to reserve a different character per level, and they
    // survive a line wrap. An UNLABELLED group has no name to attach its parts to, so they join
    // the parent's list directly.
    //
    // REJECTED alternative (fix round 1): letting an unlabelled group adopt its first child's
    // label, so a `WEEKTIME_FIELDS` day row would read `maandag (Maandag van: 08:00 · …)`.
    // Across the seven configs as they stand today it would not misfire — the only unlabelled
    // groups are those day rows, whose first child IS labelled with the day. But the rule does
    // not read as "name the group after the day", it reads as "name the group after whatever
    // happens to come first", and it breaks the moment a day row gains a leading sub-field, or
    // when a module points `props.readonlyDisplay: 'composite'` at a fieldGroup we have never
    // seen. The day name is not lost without it: `props.ariaLabel` already carries it
    // ('Maandag van' / 'Maandag tot'), which is why `labelOf` reads that too.
    return { text: label ? `${label} (${inner})` : inner, isGate: false };
  }

  const text = formatDisplayValue(raw, displayContextFor(subField));
  if (!text || !label) {
    return null;
  }
  return { text: `${label}${LABEL_SEPARATOR}${text}`, isGate: isGate(subField, raw, siblings) };
}

/**
 * A checkbox that merely GATES its siblings: ticked, and named by a sibling's own
 * `props.conditions` as the field that sibling appears on.
 *
 * Structural, not value-based (fix round 2). `props.conditions[].compareField` holds a field ID,
 * and `WEEKTIME_FIELDS` wires exactly that: each day row's `enabled` checkbox carries
 * `id: 'formly_<dag>_<index>_<stamp>'`, and the `start`, `tot` and `end` siblings beside it all
 * condition on that same id. (`CRON_FIELDS` uses the identical mechanism —
 * `compareField: 'formly_473_input_id_1'` is the `repeat` select's `id` — so this is the
 * project's condition wiring, not a shape invented for these day rows.)
 *
 * Asking the config instead of only the value is what keeps a meaningful boolean: a `spoed: true`
 * that nothing conditions on is not a gate and keeps reading `Spoed: Ja` beside its filled
 * siblings.
 */
function isGate(subField: FormlyFieldConfig, value: unknown, siblings: FormlyFieldConfig[]): boolean {
  if (!isTicked(value)) {
    return false;
  }
  const id = subField.id;
  return typeof id === 'string' && id !== '' && siblings.some(other => other !== subField && conditionsOn(other, id));
}

/** Whether this sibling's `props.conditions` compare against the given field id. */
function conditionsOn(sibling: FormlyFieldConfig, id: string): boolean {
  const conditions = sibling.props?.['conditions'];
  return (
    Array.isArray(conditions) &&
    conditions.some(condition => (condition as { compareField?: unknown } | null)?.compareField === id)
  );
}

/**
 * A "ticked" leaf: the boolean the writer stores, or the string a hydrated import can carry.
 * Both spellings, for the same reason `readonlyPresenceValue` (`readonly.types.ts`) accepts both
 * — a strict `=== true` would report a hydrated all-'true'-strings answer as something else than
 * the identical answer typed in the browser.
 */
function isTicked(value: unknown): boolean {
  return value === true || value === 'true';
}

/** A sub-field whose own sub-fields are worth walking, rather than one answer of its own. */
function walksInto(subField: FormlyFieldConfig, value: unknown): boolean {
  return (
    !!subField.fieldGroup?.length &&
    isPlainObject(value) &&
    !SELF_FORMATTING_COMPOSITE_TYPES.has(String(subField.type))
  );
}

/** The sub-field's visible label, its accessible name as a fallback, or `null` for neither. */
function labelOf(subField: FormlyFieldConfig): string | null {
  const label = subField.props?.label;
  if (typeof label === 'string' && label.trim()) {
    return label.trim();
  }
  const ariaLabel = subField.props?.['ariaLabel'];
  if (typeof ariaLabel === 'string' && ariaLabel.trim()) {
    return ariaLabel.trim();
  }
  return null;
}

/** A value that can carry sub-answers: a plain object, never an array or a `Date`. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);
}
